/* eslint-disable @typescript-eslint/no-empty-function */
import {
  apiInstance,
  BuyerCookieService,
  changeBuyerSession,
  CookieService,
  recoverBuyerSession,
  registerSessionHandlers,
  trackEvent,
  watchBuyerSession,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import { useRouteRedirection } from '@diamond/sol-buyer/utils';
import * as SentryNext from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import shallow from 'zustand/shallow';

import { clearBuyerSession } from './session/clearBuyerSession';
import {
  flushSessionRecovery,
  reportBuyerSessionRecovery,
} from './session/report-session-recovery';
import { useAuthStore, useShipToStore } from './stores';

export type AuthSession = {
  session: null | {
    userId: string | null;
    userName: string | null;
    role: string | null;
    access_token: string | undefined;
  };
};

type SessionRecovery =
  | 'idle'
  | 'recovering'
  | 'temporary-failure'
  | 'redirecting';

type AuthContext = {
  loggedIn: boolean;
  isLoading: boolean;
  sessionRecovery: SessionRecovery;
  ensureSession: () => Promise<boolean>;
  expireSession: () => Promise<void>;
  login: (
    token: string,
    refreshToken: string,
    expTime: number
  ) => Promise<void>;
  logout: (token: string) => void;
} & AuthSession;

export const DefaultAuthContext: AuthContext = {
  session: null,
  loggedIn: false,
  isLoading: false,
  sessionRecovery: 'idle',
  ensureSession: async () => false,
  expireSession: async () => {},
  login: async () => {},
  logout: () => {},
};

export const AuthContext = createContext<AuthContext>(DefaultAuthContext);

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [
    access_token,
    setUserId,
    setUserName,
    setUserRole,
    setAccessToken,
    setRefreshToken,
    setUser,
  ] = useAuthStore(
    (s) => [
      s.access_token,
      s.setUserId,
      s.setUserName,
      s.setUserRole,
      s.setAccessToken,
      s.setRefreshToken,
      s.setUser,
    ],
    shallow
  );

  const [setAvailableShipTo, setSelectedShipToId] = useShipToStore(
    (s) => [s.setAvailableShipTo, s.setSelectedShipToId],
    shallow
  );

  const router = useRouter();
  const { getPathnameRedirects } = useRouteRedirection();

  const isTokenExist =
    access_token !== null && access_token !== undefined && access_token !== '';
  const [loggedIn, setLoggedIn] = useState(isTokenExist);
  const [sessionRecovery, setSessionRecovery] =
    useState<SessionRecovery>('idle');
  const ensurePromise = useRef<Promise<boolean> | null>(null);
  const [session, setSession] = useState<AuthSession['session']>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const cleanupPromise = useRef<Promise<void> | null>(null);
  const endSession = useCallback(
    (expired: boolean) => {
      if (cleanupPromise.current) return cleanupPromise.current;
      const trigger = expired ? 'session_expiry' : 'manual_logout';
      reportBuyerSessionRecovery({ name: 'cleanup_started', trigger });
      setSessionRecovery('redirecting');
      setLoggedIn(false);
      setSession(null);
      cleanupPromise.current = (async () => {
        try {
          await clearBuyerSession(queryClient);
          reportBuyerSessionRecovery({ name: 'cleanup_succeeded', trigger });
        } catch {
          reportBuyerSessionRecovery({ name: 'cleanup_failed', trigger });
        }
        reportBuyerSessionRecovery({ name: 'redirect_requested', trigger });
        await flushSessionRecovery();
        try {
          window.location.assign(
            expired ? '/login?reason=session-expired' : '/login'
          );
        } catch (error) {
          reportBuyerSessionRecovery({ name: 'redirect_failed', trigger });
          throw error;
        }
      })();
      return cleanupPromise.current;
    },
    [queryClient]
  );

  const expireSession = useCallback(async () => {
    if (cleanupPromise.current) return cleanupPromise.current;
    let cleanup: Promise<void> | undefined;
    try {
      await changeBuyerSession(() => {
        cleanup = endSession(true);
      });
    } catch {
      cleanup = endSession(true);
    }
    await cleanup;
  }, [endSession]);

  const ensureSession = useCallback((): Promise<boolean> => {
    if (router.pathname === '/login' || cleanupPromise.current) {
      return Promise.resolve(false);
    }
    if (ensurePromise.current) return ensurePromise.current;
    if (
      useAuthStore.persist.hasHydrated() &&
      (CookieService.getJwtToken() || useAuthStore.getState().access_token)
    ) {
      setLoggedIn(true);
      setSessionRecovery('idle');
      return Promise.resolve(true);
    }
    setSessionRecovery('recovering');
    ensurePromise.current = (async () => {
      const persist = useAuthStore.persist;
      await new Promise<void>((resolve) => {
        if (persist.hasHydrated()) {
          resolve();
          return;
        }
        const unsubscribe = persist.onFinishHydration(() => {
          unsubscribe();
          resolve();
        });
      });
      if (cleanupPromise.current) return false;
      const token =
        CookieService.getJwtToken() || useAuthStore.getState().access_token;
      if (token) {
        setLoggedIn(true);
        setSessionRecovery('idle');
        return true;
      }
      if (!CookieService.getRefreshToken()) {
        await expireSession();
        return false;
      }
      const result = await recoverBuyerSession();
      if (cleanupPromise.current) return false;
      if (result.status === 'success') {
        setLoggedIn(true);
        setSessionRecovery('idle');
        return true;
      }
      setSessionRecovery('temporary-failure');
      return false;
    })().finally(() => {
      ensurePromise.current = null;
    });
    return ensurePromise.current;
  }, [expireSession, router.pathname]);

  useEffect(
    () =>
      registerSessionHandlers({
        coordinateAcrossTabs: true,
        onRecoveryEvent: (event) => reportBuyerSessionRecovery(event, false),
        isSessionActive: () => cleanupPromise.current === null,
        shouldRecoverSession: () =>
          Boolean(
            CookieService.getJwtToken() ||
              useAuthStore.getState().access_token ||
              CookieService.getRefreshToken()
          ) &&
          router.pathname !== '/login' &&
          cleanupPromise.current === null,
        onInvalidSession: () => endSession(true),
        onTokensRefreshed: ({ token, refresh_token }) => {
          setLoggedIn(true);
          setAccessToken(token);
          setRefreshToken(refresh_token);
          setSession((current) =>
            current ? { ...current, access_token: token } : null
          );
        },
      }),
    [endSession, loggedIn, router.pathname, setAccessToken, setRefreshToken]
  );

  useEffect(() => {
    if (!loggedIn) return;
    return watchBuyerSession(() => window.location.reload());
  }, [loggedIn]);

  const login = async (
    token: string,
    refreshToken: string,
    expTime: number
  ) => {
    setIsLoading(true);
    try {
      const generation = await changeBuyerSession(() => {});
      if (!generation)
        throw new Error('Sesi telah berubah. Silakan masuk kembali.');
      const { data: userData } = await apiInstance(token).get('/account/me');
      const committed = await changeBuyerSession(() => {
        const user_name = `${userData.first_name} ${userData.last_name}`;

        setUserId(userData.id);
        setUserName(user_name);
        setUserRole(userData.role);
        setAccessToken(token);
        setRefreshToken(refreshToken);

        setUser({
          id: userData.id,
          email: userData.email,
        });

        setAvailableShipTo(userData.ship_tos);
        setSelectedShipToId('');

        setSession({
          userId: userData.id,
          userName: user_name,
          role: userData.role,
          access_token: token,
        });

        BuyerCookieService.setAuthData(token, refreshToken, userData.role);
        setLoggedIn(true);
      }, generation);
      if (!committed)
        throw new Error('Sesi telah berubah. Silakan masuk kembali.');

      await router
        .push({
          pathname: getPathnameRedirects(),
        })
        .then(() => {
          trackEvent(TRACKING_EVENTS.DIAMOND_VISIT, { platform: 'website' });
        })
        .catch((err) => {
          router.reload();
          SentryNext.captureException(new Error(err), (scope) => {
            scope.setTag('router', 'login');
            scope.setLevel('error');
            return scope;
          });
        });
    } finally {
      setIsLoading(false);
    }
  };

  // Clear stored cookies and set false for loggedIn state.
  const logout = async (token: string) => {
    const request = apiInstance(token)
      .delete('/auth/logout')
      .catch((error: unknown) => ({ error }));
    let cleanup: Promise<void> | undefined;
    const beginCleanup = () => {
      cleanup = endSession(false);
    };
    try {
      await changeBuyerSession(beginCleanup);
    } catch {
      // Local logout must still clear credentials when coordination is unavailable.
      beginCleanup();
    }
    try {
      const result = await request;
      if ('error' in result) throw result.error;
    } finally {
      await cleanup;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        loggedIn,
        login,
        logout,
        isLoading,
        sessionRecovery,
        ensureSession,
        expireSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthentication(): AuthContext {
  const context = useContext(AuthContext);
  return context;
}
